import React, { useMemo, useState, useCallback } from 'react';
import styled from 'styled-components';
import { media } from '../theme/tokens';
import { KpiStrip } from '../components/KpiStrip';
import { DataTable } from '../components/DataTable';
import { SimpleSelect } from '../components/SimpleSelect';
import {
  useNotationHeaderCompare,
  useNotationCompare
} from 'apiTyped/hooks/notes.query';

// --- Compare View ---

type DisplayMode = 'primeur' | 'livrable';

export const CompareView: React.FC = () => {
  const { isLoading: isLoadingHeader, headerCompare } =
    useNotationHeaderCompare();
  const [selectedWine, setSelectedWine] = useState<string>('');
  const [selectedVintage, setSelectedVintage] = useState<string>('');
  const [display, setDisplay] = useState<DisplayMode>('primeur');
  const [rankFilter, setRankFilter] = useState<string[]>([]);
  const [appellationFilter, setAppellationFilter] = useState<string[]>([]);

  const { isLoading: isLoadingCompare, compare } = useNotationCompare({
    rank: rankFilter,
    appellation: appellationFilter
  });

  const header = headerCompare as any;
  const clientWines: any[] = header?.client_wines || [];
  const appellations: any[] = header?.appelations || [];
  const classements: any[] = header?.classements || [];

  // Auto-select first wine if not set
  const effectiveWine =
    selectedWine || (clientWines.length > 0 ? clientWines[0].wine_name : '');

  // Merge and pivot data: one row per wine+vintage, columns per journalist
  const { rows, journalists, vintages, kpis } = useMemo(() => {
    const allNotes: any[] = (compare as any)?.all_notes || [];
    const wxrNotes: any[] = (compare as any)?.wxr_notes || [];

    // Get unique journalists
    const journalistSet = new Set<string>();
    allNotes.forEach((n: any) => {
      if (n.journalist_short_name) journalistSet.add(n.journalist_short_name);
    });
    const journalists = [...journalistSet].sort();

    // Filter by display mode
    const noteType = display === 'primeur' ? 'Primeur' : 'Livrable';
    const filteredNotes = allNotes.filter(
      (n: any) => n.french_note_type === noteType || !n.french_note_type
    );

    // Aggregate into rows keyed by wine+vintage
    const rowMap = new Map<string, any>();

    filteredNotes.forEach((n: any) => {
      const key = `${n.wine_name}::${n.vintage}`;
      if (!rowMap.has(key)) {
        rowMap.set(key, { wine_name: n.wine_name, vintage: n.vintage });
      }
      const row = rowMap.get(key)!;
      if (n.journalist_short_name) {
        row[`score_${n.journalist_short_name}`] = n.note_filtered;
        row[`raw_${n.journalist_short_name}`] = n.float_note_filtered;
      }
    });

    // Merge WXR scores
    wxrNotes.forEach((w: any) => {
      const key = `${w.wine_name}::${w.vintage}`;
      if (!rowMap.has(key)) {
        rowMap.set(key, { wine_name: w.wine_name, vintage: w.vintage });
      }
      const row = rowMap.get(key)!;
      row.wxr = display === 'primeur' ? w.wxr_primeur : w.wxr;
    });

    const rows = [...rowMap.values()];

    // Extract vintages
    const vintageSet = new Set<string>();
    rows.forEach((r) => {
      if (r.vintage) vintageSet.add(String(r.vintage));
    });
    const vintages = [...vintageSet].sort().reverse();

    // KPIs
    const clientWineNames = new Set(clientWines.map((w: any) => w.wine_name));
    const clientRows = rows.filter((r) => clientWineNames.has(r.wine_name));
    const wxrScores = clientRows.map((r) => r.wxr).filter(Boolean);
    const bestWxr = wxrScores.length > 0 ? Math.max(...wxrScores) : null;
    const avgWxr =
      wxrScores.length > 0
        ? wxrScores.reduce((a: number, b: number) => a + b, 0) /
          wxrScores.length
        : null;

    return {
      rows,
      journalists,
      vintages,
      kpis: { bestWxr, avgWxr, totalWines: rows.length }
    };
  }, [compare, display, clientWines]);

  // Filter rows by wine and vintage
  const filteredRows = useMemo(() => {
    let result = rows;
    if (effectiveWine) {
      // Show the selected wine and its rivals
      const wineEntry = clientWines.find(
        (w: any) => w.wine_name === effectiveWine
      );
      const rivals: string[] = wineEntry?.rivals || [];
      const allowed = new Set([effectiveWine, ...rivals]);
      result = result.filter((r) => allowed.has(r.wine_name));
    }
    if (selectedVintage) {
      result = result.filter((r) => String(r.vintage) === selectedVintage);
    }
    return result;
  }, [rows, effectiveWine, selectedVintage, clientWines]);

  // Client wine names for highlighting
  const clientWineNames = useMemo(
    () => new Set(clientWines.map((w: any) => w.wine_name)),
    [clientWines]
  );

  const columns = useMemo(() => {
    const cols: any[] = [
      {
        key: 'wine_name',
        header: 'Wine',
        sortable: true,
        width: '22%',
        render: (row: any) => (
          <WineName $isClient={clientWineNames.has(row.wine_name)}>
            {row.wine_name || '—'}
          </WineName>
        )
      },
      {
        key: 'vintage',
        header: 'Vintage',
        sortable: true,
        width: '8%',
        render: (row: any) => row.vintage || '—'
      },
      {
        key: 'wxr',
        header: 'WxR',
        sortable: true,
        width: '8%',
        render: (row: any) => (
          <ScoreCell $highlight={!!row.wxr}>
            {row.wxr ? row.wxr.toFixed(1) : '—'}
          </ScoreCell>
        )
      },
      ...journalists.map((j) => ({
        key: `score_${j}`,
        header: j,
        sortable: true,
        width: `${Math.max(60 / journalists.length, 8)}%`,
        render: (row: any) => (
          <ScoreCell $highlight={false}>{row[`score_${j}`] || '—'}</ScoreCell>
        )
      }))
    ];
    return cols;
  }, [journalists, clientWineNames]);

  const handleResetFilters = useCallback(() => {
    setSelectedWine('');
    setSelectedVintage('');
    setRankFilter([]);
    setAppellationFilter([]);
  }, []);

  const isLoading = isLoadingHeader || isLoadingCompare;

  return (
    <>
      {/* KPIs */}
      <KpiStrip
        items={[
          { label: 'Wines compared', value: kpis.totalWines },
          {
            label: 'Best WxR',
            value: kpis.bestWxr ? kpis.bestWxr.toFixed(1) : '—'
          },
          {
            label: 'Avg WxR',
            value: kpis.avgWxr ? kpis.avgWxr.toFixed(1) : '—'
          },
          { label: 'Critics', value: journalists.length }
        ]}
      />

      {/* Filters */}
      <FiltersBar>
        <SimpleSelect
          options={[
            { value: '', label: 'All wines' },
            ...clientWines.map((w: any) => ({
              value: w.wine_name,
              label: w.wine_name
            }))
          ]}
          value={effectiveWine}
          onChange={(val) => setSelectedWine(val as string)}
          minWidth={130}
        />

        <SimpleSelect
          options={[
            { value: '', label: 'All vintages' },
            ...vintages.map((v) => ({ value: v, label: v }))
          ]}
          value={selectedVintage}
          onChange={(val) => setSelectedVintage(val as string)}
          minWidth={120}
        />

        <SimpleSelect
          options={[
            { value: '', label: 'All appellations' },
            ...appellations.map((a: any) => ({ value: a.id, label: a.name }))
          ]}
          value={appellationFilter[0] || ''}
          onChange={(val) => setAppellationFilter(val ? [val as string] : [])}
          minWidth={140}
        />

        <SimpleSelect
          options={[
            { value: '', label: 'All ranks' },
            ...classements.map((c: any) => ({ value: c.id, label: c.name }))
          ]}
          value={rankFilter[0] || ''}
          onChange={(val) => setRankFilter(val ? [val as string] : [])}
          minWidth={110}
        />

        <DisplayToggle>
          <ToggleBtn
            $active={display === 'primeur'}
            onClick={() => setDisplay('primeur')}
          >
            Primeur
          </ToggleBtn>
          <ToggleBtn
            $active={display === 'livrable'}
            onClick={() => setDisplay('livrable')}
          >
            Livrable
          </ToggleBtn>
        </DisplayToggle>

        {(selectedWine ||
          selectedVintage ||
          rankFilter.length > 0 ||
          appellationFilter.length > 0) && (
          <ResetButton onClick={handleResetFilters}>Reset</ResetButton>
        )}
      </FiltersBar>

      {/* Table */}
      {isLoading ? (
        <LoadingWrap>Loading comparison data...</LoadingWrap>
      ) : (
        <DataTable
          columns={columns}
          data={filteredRows}
          emptyMessage="No comparison data available for the current filters."
        />
      )}
    </>
  );
};

// --- Styled Components ---

const FiltersBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
  padding: 12px 16px;
  background: var(--white);
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
`;

const DisplayToggle = styled.div`
  display: inline-flex;
  border: 1px solid var(--line);
  border-radius: var(--radius-sm);
  overflow: hidden;
  margin-left: auto;
`;

const ToggleBtn = styled.button<{ $active: boolean }>`
  border: none;
  background: ${({ $active }) =>
    $active ? 'var(--terracotta)' : 'var(--white)'};
  color: ${({ $active }) => ($active ? 'var(--white)' : 'var(--ink-muted)')};
  padding: 6px 14px;
  font-family: var(--font-sans);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: ${({ $active }) =>
      $active ? 'var(--terracotta)' : 'var(--paper)'};
  }
`;

const ResetButton = styled.button`
  background: none;
  border: 1px solid var(--line);
  border-radius: var(--radius-sm);
  padding: 6px 12px;
  font-family: var(--font-sans);
  font-size: 12px;
  font-weight: 500;
  color: var(--terracotta);
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: var(--terracotta);
    background: var(--terracotta-soft);
  }
`;

const LoadingWrap = styled.div`
  padding: 48px 0;
  text-align: center;
  color: var(--ink-faint);
  font-size: 13px;
`;

const WineName = styled.span<{ $isClient: boolean }>`
  font-weight: ${({ $isClient }) => ($isClient ? 600 : 400)};
  color: ${({ $isClient }) => ($isClient ? 'var(--terracotta)' : 'var(--ink)')};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
  display: inline-block;
`;

const ScoreCell = styled.span<{ $highlight: boolean }>`
  font-weight: ${({ $highlight }) => ($highlight ? 700 : 500)};
  font-family: var(--font-mono, var(--font-sans));
  font-size: 12px;
  color: ${({ $highlight }) =>
    $highlight ? 'var(--terracotta)' : 'var(--ink)'};
`;
